// PreviewEditorHome/TemplateEditor.js - NATIVE HTML5 DRAG & DROP

import React, { useState, useCallback, useRef, useEffect } from 'react';
import TemplateRow from './TemplateRow';
import CustomMessageModal from './CustomMessageModal';
import ButtonGroupModal from './ButtonGroupModal';
import {
  createButtonStep,
  createButtonGroupStep,
  createStyleStep,
  createStructuralStep,
  createMediaStep,
  createCombinedStep,
  cleanSelectors,
  isButtonStep,
  isButtonGroupStep,
  isStyleStep,
  isStructuralStep,
  isMediaStep,
  isCombinedStep
} from '../PreviewTester/utils/transformationSchema';

const countRenderedElements = (content) => {
  if (!content) return 0;
  const trimmed = content.trim();
  if (trimmed === '') return 0;
  if (trimmed.startsWith('{%') && trimmed.endsWith('%}')) return 0;
  if (trimmed.startsWith('{#') || trimmed.startsWith('{% comment')) return 0;
  if (/^[A-Za-z\s]+:$/.test(trimmed)) return 1;
  const kvMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[-:=>]\s*(.+)$/);
  if (kvMatch) return 3;
  return 1;
};

// ============================================================
// GENERATE SELECTOR PATH FOR ROW
// ============================================================
//
// Emits a path for a template-line row, relative to `.item-ui-screen`.
//
// RULES OBSERVED:
//   R1. Path is relative to .item-ui-screen (starts at the React
//       wrapper segment `div:nth-child(1)`).
//   R2. If the row is inside a loop container, the path starts with
//       `wrapper > contentWraps > loop-container > iter-wrapper > row`
//       — the loop container's position is discovered by walking the
//       template lines.
//   R3. The React wrapper segment is ALWAYS the first segment.
//   R4. This function produces row-level (shallow) paths — one
//       segment per DOM element between `.contentWraps` and the row.
//       Deep (leaf-level) selectors are produced elsewhere.
//
// KEY INSIGHT — WHICH LOOPS ARE WRAPPED:
//
//   • Case 1/3 (root array, or object-with-nested-array):
//       The OUTER loop is stripped during render. Each screen IS one
//       iteration of the outer loop. So the outer loop has NO
//       `.for-loop-started` container in the DOM.
//
//       A row at depth 1 (inside outer loop, outside inner loop):
//         path = wrapper > contentWraps > row
//
//       A row at depth 2 (inside inner loop):
//         path = wrapper > contentWraps > inner-loop > iter-wrapper > row
//
//   • Case 2 (flat object with single screen):
//       The OUTER loop IS wrapped. A row at depth 1 lives inside
//       the loop container:
//         path = wrapper > contentWraps > loop-container > iter-wrapper > row
//
//   General rule: a loop contributes a DOM container iff
//       isCase2 === true   (its own depth === 0)
//     OR
//       its own depth >= 1 (any inner loop)
// ============================================================
const generateSelectorPathForRow = (rowIndex, lines, isCase2 = false) => {
  console.log('[generateSelectorPathForRow] START', {
    rowIndex,
    totalLines: lines?.length,
    isCase2,
  });

  if (!lines || rowIndex < 0 || rowIndex >= lines.length) {
    console.log('[generateSelectorPathForRow] ❌ Invalid rowIndex or lines');
    return '';
  }

  const row = lines[rowIndex];
  if (!row) {
    console.log('[generateSelectorPathForRow] ❌ Row not found');
    return '';
  }

  const depth = row.depth || 0;
  console.log('[generateSelectorPathForRow] 📊 Target row:', {
    id: row.id,
    type: row.type,
    depth,
    content: row.content?.substring(0, 30),
  });

  // ============================================================
  // Base path: screen > wrapper > contentWraps
  // ============================================================
  const pathParts = ['div:nth-child(1)', 'div:nth-child(1)'];

  // ============================================================
  // Find the DEEPEST enclosing loop for this row.
  // ============================================================
  let targetLoopStartIdx = -1;
  let targetLoopDepth = -1;

  for (let i = rowIndex - 1; i >= 0; i--) {
    const prevRow = lines[i];
    if (!prevRow) continue;
    if (prevRow.type !== 'for_loop_start') continue;

    const prevDepth = prevRow.depth || 0;
    if (prevDepth >= depth) continue;

    let endIdx = -1;
    let balance = 0;
    for (let j = i + 1; j < lines.length; j++) {
      const r = lines[j];
      if (!r) continue;
      if (r.type === 'for_loop_start') balance++;
      else if (r.type === 'for_loop_end') {
        if (balance === 0) { endIdx = j; break; }
        balance--;
      }
    }

    if (endIdx !== -1 && endIdx > rowIndex) {
      targetLoopStartIdx = i;
      targetLoopDepth = prevDepth;
      break;
    }
  }

  console.log('[generateSelectorPathForRow] 🔎 Enclosing loop:', {
    targetLoopStartIdx,
    targetLoopDepth,
  });

  // ============================================================
  // BRANCH 1 — ROW IS INSIDE A LOOP
  // ============================================================
  if (targetLoopStartIdx !== -1) {
    const loopDepth = targetLoopDepth;
    const loopIsWrapped = isCase2 || loopDepth >= 1;

    console.log(
      `[generateSelectorPathForRow] 🔎 Enclosing loop is wrapped: ${loopIsWrapped}`,
    );

    // ============================================================
    // ✅ DEV-ONLY GUARD — 7A
    // INVARIANT: loopIsWrapped must match the enhancer's rule:
    //              isInner || (isCase2 && loopDepth === 1)
    //            where isInner = loopDepth >= 1.
    //
    // If this diverges, path generation and DOM shape disagree —
    // every selector for this row will be off by two segments.
    //
    // Owned by: enhanceTemplateWithLoopWrappers in templateEnhancer.js
    // ============================================================
    if (process.env.NODE_ENV !== "production") {
      const expectedWrapped =
        loopDepth >= 1 || (isCase2 && loopDepth === 0);
      if (loopIsWrapped !== expectedWrapped) {
        console.warn(
          "[INVARIANT:7A] generateSelectorPathForRow wrapped-loop decision diverges from enhancer.",
          {
            loopDepth,
            isCase2,
            loopIsWrapped,
            expectedWrapped,
            rowIndex,
            rowDepth: depth,
          },
        );
      }
    }

    if (loopIsWrapped) {
      // Loop container position among its siblings
      let wrapperPos = 1;
      for (let i = 0; i < targetLoopStartIdx; i++) {
        const prevRow = lines[i];
        if (!prevRow) continue;
        if ((prevRow.depth || 0) !== loopDepth) continue;
        if (isEmptyRow(prevRow)) continue;

        if (prevRow.type === 'for_loop_start') {
          wrapperPos++;
          continue;
        }
        if (isTemplateTag(prevRow)) continue;
        wrapperPos++;
      }
      pathParts.push(`div:nth-child(${wrapperPos})`);
      console.log(
        `[generateSelectorPathForRow] 📊 Loop container position: ${wrapperPos}`,
      );

      // Per-iteration wrapper
      pathParts.push('div:nth-child(1)');
    }

    // Row position WITHIN the iteration
    let targetPosInIter = 1;
    for (let i = targetLoopStartIdx + 1; i < rowIndex; i++) {
      const prevRow = lines[i];
      if (!prevRow) continue;
      if ((prevRow.depth || 0) !== depth) continue;
      if (isEmptyRow(prevRow)) continue;

      if (prevRow.type === 'for_loop_start') {
        targetPosInIter++;
        continue;
      }
      if (isTemplateTag(prevRow)) continue;
      targetPosInIter++;
    }
    pathParts.push(`div:nth-child(${targetPosInIter})`);

    // ============================================================
    // ✅ DEV-ONLY GUARD — 7B
    // INVARIANT: targetPosInIter >= 1 (nth-child is 1-based).
    //
    // A value of 0 means the counting loop missed the target row
    // entirely (typically because the row itself was skipped by
    // isEmptyRow / isTemplateTag). The resulting path would resolve
    // to a non-existent element.
    // ============================================================
    if (process.env.NODE_ENV !== "production") {
      if (!Number.isInteger(targetPosInIter) || targetPosInIter < 1) {
        console.warn(
          "[INVARIANT:7B] generateSelectorPathForRow computed invalid target position.",
          {
            targetPosInIter,
            rowIndex,
            depth,
            targetLoopStartIdx,
            targetLoopDepth,
            rowType: row.type,
          },
        );
      }
    }

    const result = pathParts.join(' > ');
    console.log(
      '[generateSelectorPathForRow] ✅ Generated loop-relative path:',
      result,
    );
    return result;
  }

  // ============================================================
  // BRANCH 2 — ROW IS NOT INSIDE ANY LOOP
  // ============================================================
  let targetPos = 1;
  for (let i = 0; i < rowIndex; i++) {
    const prevRow = lines[i];
    if (!prevRow) continue;
    if ((prevRow.depth || 0) !== depth) continue;
    if (isEmptyRow(prevRow)) continue;

    if (prevRow.type === 'for_loop_start') {
      targetPos++;
      continue;
    }
    if (isTemplateTag(prevRow)) continue;
    targetPos++;
  }
  pathParts.push(`div:nth-child(${targetPos})`);

  // ============================================================
  // ✅ DEV-ONLY GUARD — 7B (second insertion — non-loop branch)
  // Same invariant as the loop branch, applied to targetPos.
  // ============================================================
  if (process.env.NODE_ENV !== "production") {
    if (!Number.isInteger(targetPos) || targetPos < 1) {
      console.warn(
        "[INVARIANT:7B] generateSelectorPathForRow computed invalid target position (non-loop).",
        {
          targetPos,
          rowIndex,
          depth,
          rowType: row.type,
        },
      );
    }
  }

  const result = pathParts.join(' > ');
  console.log(
    '[generateSelectorPathForRow] ✅ Generated non-loop path:',
    result,
  );
  return result;
};



// Helper functions
// Helper functions
const isEmptyRow = (row) => {
  if (!row) return true;
  if (row.type === 'empty') return true;
  if (!row.content || row.content.trim() === '') return true;
  return false;
};

const isTemplateTag = (row) => {
  if (!row) return true;
  const templateTagTypes = [
    'for_loop_start',
    'for_loop_end',
    'if_start',
    'if_end',
    'elif',
    'else',
    'empty',
  ];
  return templateTagTypes.includes(row.type);
};


const TemplateEditor = ({
  lines,
  onLinesChange,
  isEditMode,
  onToggleEditMode,
  availableVariables,
  onTemplateUpdate,
  onTrackChange,
  parseTemplateForEdit,
  parsedSample,
  isCase2 = false,
  onAddTransformationStep,
  onRemoveTransformationStep,
  onUpdateTransformationStep,
  transformationSteps = [],
}) => {
  console.log("isCadddddse2", isCase2)
  const [showCustomMessageModal, setShowCustomMessageModal] = useState(false);
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [showButtonGroupModal, setShowButtonGroupModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [customMessagePosition, setCustomMessagePosition] = useState(null);
  const [buttonPosition, setButtonPosition] = useState(null);
  const [buttonGroupPosition, setButtonGroupPosition] = useState(null);

  // ============================================================
  // ✅ Forbidden endfor detection + safe default position
  // ============================================================

  /**
   * Determine which `{% endfor %}` lines are the OUTERMOST loop
   * closers for Case 1 / Case 3. Content placed after these lines
   * would exist OUTSIDE the screen(s) — so they are not valid
   * insertion targets.
   *
   * Case 2 → no restriction (inner loops only).
   */
  const getForbiddenEndforIndices = useCallback(() => {
    const forbidden = new Set();

    // Case 2 → nothing forbidden
    if (isCase2) return forbidden;

    // Case 1 or Case 3 → find the FIRST (outermost) `{% endfor %}`
    let depth = 0;
    let outerEndforFound = false;

    for (let i = 0; i < lines.length; i++) {
      const row = lines[i];
      if (!row) continue;

      const content = (row.content || '').trim();
      const isForStart = row.type === 'for_loop_start' || /^\{%\s*for\s+/.test(content);
      const isForEnd = row.type === 'for_loop_end' || /^\{%\s*endfor\s*%\}/.test(content);

      if (isForStart) {
        depth++;
      } else if (isForEnd) {
        depth--;
        if (depth === 0 && !outerEndforFound) {
          forbidden.add(i);
          outerEndforFound = true;
        }
      }
    }

    console.log('[TemplateEditor] 🚫 Forbidden endfor indices:', [...forbidden]);
    return forbidden;
  }, [lines, isCase2]);

  /**
   * Find the last position that is NOT a forbidden endfor.
   * Used as the default position when opening modals.
   */
  const getSafeDefaultPosition = useCallback(() => {
    const forbidden = getForbiddenEndforIndices();

    for (let i = lines.length - 1; i >= 0; i--) {
      if (!forbidden.has(i)) return i;
    }
    return 0; // fallback
  }, [lines, getForbiddenEndforIndices]);

  // ============================================================
  // Add Custom Message
  // ============================================================
  const handleAddCustomMessage = useCallback((message, variables, position) => {
    let insertPosition = position !== undefined ? position : selectedPosition;
    if (insertPosition === null) {
      console.warn('[TemplateEditor] No position selected for custom message');
      return;
    }

    // ✅ Defensive guard: redirect if position is a forbidden outer endfor
    const forbidden = getForbiddenEndforIndices();
    if (forbidden.has(insertPosition)) {
      console.warn('[TemplateEditor] 🚫 Redirecting insert from forbidden outer endfor.');
      for (let i = insertPosition - 1; i >= 0; i--) {
        if (!forbidden.has(i)) { insertPosition = i; break; }
      }
    }

    const depth = lines[insertPosition]?.depth || 0;
    const insertIndex = insertPosition + 1;

    const newRow = {
      id: `custom-${Date.now()}`,
      content: message,
      trimmed: message.trim(),
      type: 'custom',
      indent: lines[insertPosition]?.indent || '  ',
      isLoopStart: false,
      isLoopEnd: false,
      loopVariable: null,
      loopArray: null,
      condition: null,
      variables: variables,
      isInForLoop: lines[insertPosition]?.isInForLoop || false,
      isInIfBlock: lines[insertPosition]?.isInIfBlock || false,
      depth: depth,
      originalIndex: -1,
      isCustom: true,
      customId: `custom-${Date.now()}`,
      customMessage: message,
    };

    const tempLines = [...lines];
    tempLines.splice(insertIndex, 0, newRow);
    const selectorPath = generateSelectorPathForRow(insertIndex, tempLines, isCase2);

    if (onTrackChange) {
      onTrackChange({
        type: 'add',
        selectorPath: selectorPath,
      });
    }

    const newLines = [...lines];
    newLines.splice(insertIndex, 0, newRow);
    onLinesChange(newLines);
    onTemplateUpdate(newLines);
    setShowCustomMessageModal(false);
    setSelectedPosition(null);
    setCustomMessagePosition(null);
  }, [lines, selectedPosition, onLinesChange, onTemplateUpdate, onTrackChange, isCase2, getForbiddenEndforIndices]);

  // ============================================================
  // Add Button - UPDATED with minimal schema
  // ============================================================
  const handleAddButton = useCallback((message, variables, position) => {
    console.log('[🔘 BUTTON FLOW] handleAddButton START');
    console.log('[🔘 BUTTON FLOW] message:', message);
    console.log('[🔘 BUTTON FLOW] selectedPosition:', selectedPosition);
    console.log('[🔘 BUTTON FLOW] lines length before:', lines.length);

    let insertPosition = position !== undefined ? position : selectedPosition;
    if (insertPosition === null) {
      console.warn('[🔘 BUTTON FLOW] No position selected for button');
      return;
    }

    // ✅ Defensive guard: redirect if position is a forbidden outer endfor
    const forbidden = getForbiddenEndforIndices();
    if (forbidden.has(insertPosition)) {
      console.warn('[🔘 BUTTON FLOW] 🚫 Redirecting insert from forbidden outer endfor.');
      for (let i = insertPosition - 1; i >= 0; i--) {
        if (!forbidden.has(i)) { insertPosition = i; break; }
      }
    }

    const depth = lines[insertPosition]?.depth || 0;
    const insertIndex = insertPosition + 1;

    const newRow = {
      id: `button-${Date.now()}`,
      content: message,
      trimmed: message.trim(),
      type: 'button',
      indent: lines[insertPosition]?.indent || '  ',
      isLoopStart: false,
      isLoopEnd: false,
      loopVariable: null,
      loopArray: null,
      condition: null,
      variables: variables,
      isInForLoop: lines[insertPosition]?.isInForLoop || false,
      isInIfBlock: lines[insertPosition]?.isInIfBlock || false,
      depth: depth,
      originalIndex: -1,
      isCustom: true,
      isButton: true,
      customId: `button-${Date.now()}`,
      customMessage: message,
      buttonText: message,
      needsTransformationStep: true,
    };

    console.log('[🔘 BUTTON FLOW] newRow created:', {
      id: newRow.id,
      type: newRow.type,
      isButton: newRow.isButton,
      content: newRow.content,
      buttonText: newRow.buttonText,
    });

    const tempLines = [...lines];
    tempLines.splice(insertIndex, 0, newRow);
    const selectorPath = generateSelectorPathForRow(insertIndex, tempLines, isCase2);

    console.log('[🔘 BUTTON FLOW] selectorPath:', selectorPath);

    // Track the change for recalculation
    if (onTrackChange) {
      onTrackChange({
        type: 'add',
        selectorPath: selectorPath,
        isButton: true,
        buttonText: message,
      });
    }

    // Update template lines
    const newLines = [...lines];
    newLines.splice(insertIndex, 0, newRow);

    console.log('[🔘 BUTTON FLOW] newLines length:', newLines.length);
    console.log('[🔘 BUTTON FLOW] newLines rows:');
    newLines.forEach((row, i) => {
      console.log(`  [${i}] type: ${row.type}, content: "${row.content?.substring(0, 30)}", isButton: ${row.isButton}`);
    });

    onLinesChange(newLines);
    onTemplateUpdate(newLines);

    // ============================================================
    // ✅ Create minimal button step using schema helper
    // ============================================================
    const buttonStep = createButtonStep(
      message,
      selectorPath,
      `btn_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
    );

    console.log('[🔘 BUTTON FLOW] Created minimal button step:', buttonStep);

    // ✅ Add the button step to transformation rules
    if (onAddTransformationStep) {
      console.log('[🔘 BUTTON FLOW] Adding button step via onAddTransformationStep');
      onAddTransformationStep(buttonStep);
    } else {
      console.warn('[🔘 BUTTON FLOW] onAddTransformationStep is not available, button step not added');
    }

    // Close the modal and reset state
    setShowButtonModal(false);
    setSelectedPosition(null);
    setButtonPosition(null);

    console.log('[🔘 BUTTON FLOW] handleAddButton END');
  }, [lines, selectedPosition, onLinesChange, onTemplateUpdate, onTrackChange, onAddTransformationStep, isCase2, getForbiddenEndforIndices]);

  // ============================================================
  // Add Button Group - UPDATED with minimal schema
  // ============================================================
  const handleAddButtonGroup = useCallback((groupName, buttons, position) => {
    console.log('[🔘 BUTTON GROUP] Adding button group:', { groupName, buttons, position });

    let insertPosition = position !== undefined ? position : buttonGroupPosition;
    if (insertPosition === null) {
      console.warn('[🔘 BUTTON GROUP] No position selected for button group');
      return;
    }

    // ✅ Defensive guard: redirect if position is a forbidden outer endfor
    const forbidden = getForbiddenEndforIndices();
    if (forbidden.has(insertPosition)) {
      console.warn('[🔘 BUTTON GROUP] 🚫 Redirecting insert from forbidden outer endfor.');
      for (let i = insertPosition - 1; i >= 0; i--) {
        if (!forbidden.has(i)) { insertPosition = i; break; }
      }
    }

    const depth = lines[insertPosition]?.depth || 0;
    const insertIndex = insertPosition + 1;

    const buttonIdPrefix = Date.now();

    // Create button objects with IDs
    const buttonObjects = buttons.map((text, idx) => ({
      id: `btn-${buttonIdPrefix}-${idx}`,
      text: text,
      index: idx,
    }));

    const newRow = {
      id: `button-group-${buttonIdPrefix}`,
      content: groupName || 'Button Group',
      trimmed: groupName || 'Button Group',
      type: 'button-group',
      indent: lines[insertPosition]?.indent || '  ',
      isLoopStart: false,
      isLoopEnd: false,
      loopVariable: null,
      loopArray: null,
      condition: null,
      variables: [],
      isInForLoop: lines[insertPosition]?.isInForLoop || false,
      isInIfBlock: lines[insertPosition]?.isInIfBlock || false,
      depth: depth,
      originalIndex: -1,
      isCustom: true,
      isButtonGroup: true,
      customId: `button-group-${buttonIdPrefix}`,
      customMessage: groupName || 'Button Group',
      buttons: buttonObjects,
    };

    console.log('[🔘 BUTTON GROUP] Created row:', newRow);

    const tempLines = [...lines];
    tempLines.splice(insertIndex, 0, newRow);
    const selectorPath = generateSelectorPathForRow(insertIndex, tempLines, isCase2);

    // Track the change for recalculation
    if (onTrackChange) {
      onTrackChange({
        type: 'add',
        selectorPath: selectorPath,
        isButtonGroup: true,
        groupName: groupName,
        buttonCount: buttons.length,
      });
    }

    // ============================================================
    // ✅ Create minimal button group step using schema helper
    // ============================================================
    const groupStep = createButtonGroupStep(
      groupName || 'Button Group',
      buttons,
      selectorPath,
      `btngrp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
    );

    console.log('[🔘 BUTTON GROUP] Created minimal button group step:', groupStep);

    // Update template lines
    const newLines = [...lines];
    newLines.splice(insertIndex, 0, newRow);

    onLinesChange(newLines);
    onTemplateUpdate(newLines);

    // Add the button group step to transformation rules
    if (onAddTransformationStep) {
      setTimeout(() => {
        onAddTransformationStep(groupStep);
      }, 0);
    }

    setShowButtonGroupModal(false);
    setButtonGroupPosition(null);

    console.log('[🔘 BUTTON GROUP] handleAddButtonGroup END');
  }, [lines, buttonGroupPosition, onLinesChange, onTemplateUpdate, onTrackChange, onAddTransformationStep, isCase2, getForbiddenEndforIndices]);

  // ============================================================
  // Add Button to Group - UPDATED with minimal schema
  // ============================================================
  const handleAddButtonToGroup = useCallback((rowId) => {
    console.log('[🔘 BUTTON GROUP] Adding button to group:', rowId);

    const rowIndex = lines.findIndex(line => line.id === rowId);
    if (rowIndex === -1) return;

    const newLines = [...lines];
    let newButton = null;
    let buttonIndex = -1;

    const updatedLines = newLines.map(line => {
      if (line.id === rowId && line.type === 'button-group') {
        buttonIndex = (line.buttons?.length || 0);
        newButton = {
          id: `btn-${Date.now()}`,
          text: `Button ${buttonIndex + 1}`,
          index: buttonIndex,
        };
        return {
          ...line,
          buttons: [...(line.buttons || []), newButton],
        };
      }
      return line;
    });

    // ============================================================
    // ✅ Update the button group step with new button
    // ============================================================
    if (newButton && buttonIndex >= 0 && onUpdateTransformationStep) {
      const row = updatedLines[rowIndex];
      const selectorPath = generateSelectorPathForRow(rowIndex, updatedLines, isCase2);

      const newButtonSelector = `${selectorPath} > div:nth-child(${buttonIndex + 1})`;

      // Find the existing button group step
      const existingStep = transformationSteps.find(
        step => step.type === 'button-group' && step.groupName === row.content
      );

      if (existingStep) {
        // Create updated buttons array
        const updatedButtons = [...(existingStep.buttons || []), newButton.text];

        // Update the step
        onUpdateTransformationStep({
          id: existingStep.id,
          type: 'button-group',
          operation: 'isButtonGroup',
          groupName: existingStep.groupName,
          buttons: updatedButtons,
          selectors: existingStep.selectors || [{ start: selectorPath, end: selectorPath }],
          timestamp: new Date().toISOString()
        });
      }
    }

    onLinesChange(updatedLines);
    onTemplateUpdate(updatedLines);
  }, [lines, onLinesChange, onTemplateUpdate, onUpdateTransformationStep, transformationSteps, isCase2]);

  // ============================================================
  // Delete Button from Group - UPDATED with minimal schema
  // ============================================================
  const handleDeleteButtonFromGroup = useCallback((rowId, buttonId) => {
    console.log('[🔘 BUTTON GROUP] Deleting button from group:', rowId, buttonId);

    const rowIndex = lines.findIndex(line => line.id === rowId);
    if (rowIndex === -1) return;

    const row = lines[rowIndex];
    if (!row || row.type !== 'button-group') return;

    const buttonIndex = row.buttons?.findIndex(b => b.id === buttonId) ?? -1;
    if (buttonIndex === -1) {
      console.log('[🔘 BUTTON GROUP] Button not found:', buttonId);
      return;
    }

    const buttonText = row.buttons[buttonIndex]?.text || '';

    // Remove the button from the group
    const updatedLines = lines.map(line => {
      if (line.id === rowId && line.type === 'button-group') {
        return {
          ...line,
          buttons: line.buttons.filter(b => b.id !== buttonId),
        };
      }
      return line;
    });

    // ============================================================
    // ✅ Update the button group step (remove button)
    // ============================================================
    if (onUpdateTransformationStep) {
      // Find the existing button group step
      const existingStep = transformationSteps.find(
        step => step.type === 'button-group' && step.groupName === row.content
      );

      if (existingStep) {
        // Create updated buttons array (remove the button)
        const updatedButtons = (existingStep.buttons || []).filter(
          btn => btn !== buttonText
        );

        // Update the step
        onUpdateTransformationStep({
          id: existingStep.id,
          type: 'button-group',
          operation: 'isButtonGroup',
          groupName: existingStep.groupName,
          buttons: updatedButtons,
          selectors: existingStep.selectors || [],
          timestamp: new Date().toISOString()
        });
      }
    }

    onLinesChange(updatedLines);
    onTemplateUpdate(updatedLines);
  }, [lines, onLinesChange, onTemplateUpdate, onUpdateTransformationStep, transformationSteps]);

  // ============================================================
  // NATIVE HTML5 DRAG & DROP HANDLERS
  // ============================================================

  const handleDragStart = useCallback((e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    console.log('[TemplateEditor] 📸 Drag start at index:', index);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, hoverIndex) => {
    e.preventDefault();

    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);

    if (isNaN(dragIndex) || dragIndex === hoverIndex) {
      console.log('[TemplateEditor] ⚠️ Drop ignored - same position or invalid');
      return;
    }

    if (dragIndex < 0 || dragIndex >= lines.length || hoverIndex < 0 || hoverIndex >= lines.length) {
      console.warn('[TemplateEditor] ⚠️ Invalid indices');
      return;
    }

    const newLines = [...lines];
    const [draggedRow] = newLines.splice(dragIndex, 1);
    newLines.splice(hoverIndex, 0, draggedRow);

    const newTemplate = newLines.map(l => l.content).join('\n');
    console.log('[TemplateEditor] 🔄 Re-parsing template for correct depths do we have transformationSteps1', transformationSteps);

    const reparsedLines = parseTemplateForEdit
      ? parseTemplateForEdit(newTemplate, parsedSample, isCase2, newLines)
      : newLines;

    const oldTemplate = lines.map(l => l.content).join('\n');
    const oldReparsedLines = parseTemplateForEdit
      ? parseTemplateForEdit(oldTemplate, parsedSample, isCase2, lines)
      : lines;

    const oldSelectorPath = generateSelectorPathForRow(dragIndex, oldReparsedLines, isCase2);
    const newSelectorPath = generateSelectorPathForRow(hoverIndex, reparsedLines, isCase2);

    console.log('[TemplateEditor] 📦 Drop - from index:', dragIndex, 'to index:', hoverIndex);
    console.log('[TemplateEditor] 📦 Drop - start_path:', oldSelectorPath);
    console.log('[TemplateEditor] 📦 Drop - end_path:', newSelectorPath);

    if (onTrackChange) {
      onTrackChange({
        type: 'move',
        oldSelectorPath: oldSelectorPath,
        newSelectorPath: newSelectorPath,
      });
    }

    onLinesChange(newLines);
    onTemplateUpdate(newLines);
  }, [lines, onLinesChange, onTemplateUpdate, onTrackChange, parseTemplateForEdit, parsedSample, isCase2, transformationSteps]);

  // ============================================================
  // Delete Row - UPDATED with minimal schema
  // ============================================================
  const handleDeleteRow = useCallback((rowId) => {
    if (!isEditMode) return;

    const rowIndex = lines.findIndex(line => line.id === rowId);
    if (rowIndex === -1) {
      console.warn('[TemplateEditor] ⚠️ Row not found for deletion:', rowId);
      return;
    }

    const row = lines[rowIndex];
    const selectorPath = generateSelectorPathForRow(rowIndex, lines, isCase2);

    console.log('[TemplateEditor] 🗑️ Deleting row:', {
      rowId,
      rowType: row?.type,
      rowContent: row?.content?.substring(0, 30),
      selectorPath,
    });

    // If deleting a button or button group, also remove the corresponding step
    if (row.isButton || row.isButtonGroup) {
      const stepToRemove = transformationSteps.find(step => {
        if (isButtonStep(step) && step.buttonText === (row.buttonText || row.content)) {
          return true;
        }
        if (isButtonGroupStep(step) && step.groupName === (row.content)) {
          return true;
        }
        return false;
      });

      if (stepToRemove && onRemoveTransformationStep) {
        console.log('[TemplateEditor] 🗑️ Removing transformation step:', stepToRemove.id);
        onRemoveTransformationStep(stepToRemove.id);
      }
    }

    // ✅ ONLY track the change - recalculation handles everything
    if (onTrackChange) {
      onTrackChange({
        type: 'delete',
        selectorPath: selectorPath,
      });
    }

    // Remove the row from templateLines
    const newLines = lines.filter(line => line.id !== rowId);
    onLinesChange(newLines);
    onTemplateUpdate(newLines);

    console.log('[TemplateEditor] ✅ Row deleted, recalculation will handle transformation updates');
  }, [lines, isEditMode, onLinesChange, onTemplateUpdate, onTrackChange, transformationSteps, onRemoveTransformationStep, isCase2]);

  // ============================================================
  // Update row content
  // ============================================================
  const handleContentChange = useCallback((rowId, newContent) => {
    const rowIndex = lines.findIndex(line => line.id === rowId);
    if (rowIndex === -1) return;

    const oldRow = lines[rowIndex];
    const oldElementCount = countRenderedElements(oldRow.content);
    const newElementCount = countRenderedElements(newContent);

    if (onTrackChange && oldElementCount !== newElementCount) {
      const oldSelectorPath = generateSelectorPathForRow(rowIndex, lines, isCase2);
      const tempLines = [...lines];
      tempLines[rowIndex] = { ...tempLines[rowIndex], content: newContent, trimmed: newContent.trim() };
      const newSelectorPath = generateSelectorPathForRow(rowIndex, tempLines, isCase2);

      onTrackChange({
        type: 'update',
        oldSelectorPath: oldSelectorPath,
        newSelectorPath: newSelectorPath,
      });
    }

    const newLines = lines.map(line => {
      if (line.id === rowId) {
        return { ...line, content: newContent, trimmed: newContent.trim() };
      }
      return line;
    });

    onLinesChange(newLines);
    onTemplateUpdate(newLines);
  }, [lines, onLinesChange, onTemplateUpdate, onTrackChange, isCase2]);

  // ============================================================
  // ✅ Position suggestions — filters out the outer-loop endfor
  // for Case 1 / Case 3 so users can't place content outside screens.
  // ============================================================
  const getPositionSuggestions = useCallback(() => {
    const forbidden = getForbiddenEndforIndices();

    return lines
      .map((line, index) => {
        const isEndfor =
          line.type === 'for_loop_end' ||
          /^\{%\s*endfor\s*%\}/.test((line.content || '').trim());

        // Mark the forbidden outer endfor as disabled
        if (isEndfor && forbidden.has(index)) {
          return {
            label: `After: ${line.content.substring(0, 30)}${line.content.length > 30 ? '...' : ''} (not allowed — outer loop)`,
            value: index,
            type: line.type,
            disabled: true,
          };
        }

        return {
          label: `After: ${line.content.substring(0, 30)}${line.content.length > 30 ? '...' : ''}`,
          value: index,
          type: line.type,
        };
      });
  }, [lines, getForbiddenEndforIndices]);

  // ============================================================
  // Open modal helpers — use safe default position
  // ============================================================
  const openCustomMessageModal = () => {
    const defaultPosition = getSafeDefaultPosition();
    setSelectedPosition(defaultPosition);
    setCustomMessagePosition(defaultPosition);
    setShowCustomMessageModal(true);
  };

  const openButtonModal = () => {
    const defaultPosition = getSafeDefaultPosition();
    setSelectedPosition(defaultPosition);
    setButtonPosition(defaultPosition);
    setShowButtonModal(true);
  };

  const openButtonGroupModal = () => {
    const defaultPosition = getSafeDefaultPosition();
    setSelectedPosition(defaultPosition);
    setButtonGroupPosition(defaultPosition);
    setShowButtonGroupModal(true);
  };

  if (!isEditMode) return null;

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      border: '2px solid #4299e1',
      padding: '12px',
      marginTop: '12px',
      overflow: 'auto',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: '1px solid #e2e8f0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
            ✏️ Template Editor
          </span>
          <span style={{ fontSize: '11px', color: '#718096' }}>
            {lines.length} rows
          </span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={openCustomMessageModal}
            style={{
              padding: '4px 12px',
              backgroundColor: '#9f7aea',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: '500',
            }}
          >
            + Custom Message
          </button>
          <button
            onClick={openButtonModal}
            style={{
              padding: '4px 12px',
              backgroundColor: '#48bb78',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: '500',
            }}
          >
            + Add Button
          </button>
          <button
            onClick={openButtonGroupModal}
            style={{
              padding: '4px 12px',
              backgroundColor: '#ed8936',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: '500',
            }}
          >
            + Add Button Group
          </button>
          <button
            onClick={onToggleEditMode}
            style={{
              padding: '4px 12px',
              backgroundColor: '#fc8181',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: '500',
            }}
          >
            ✕ Exit Edit Mode
          </button>
        </div>
      </div>

      <div style={{
        fontSize: '11px',
        color: '#718096',
        padding: '6px 10px',
        backgroundColor: '#f7fafc',
        borderRadius: '4px',
        marginBottom: '10px',
      }}>
        💡 Drag rows to reorder • Click ✎ to edit • Click ✕ to delete • Use @ to insert variables
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {lines.map((row, index) => (
          <TemplateRow
            key={row.id}
            row={row}
            index={index}
            isEditMode={isEditMode}
            onDelete={handleDeleteRow}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onContentChange={handleContentChange}
            availableVariables={availableVariables}
            totalRows={lines.length}
            onAddButtonToGroup={handleAddButtonToGroup}
            onDeleteButtonFromGroup={handleDeleteButtonFromGroup}
          />
        ))}
      </div>

      {/* Custom Message Modal */}
      {showCustomMessageModal && (
        <CustomMessageModal
          onClose={() => {
            setShowCustomMessageModal(false);
            setSelectedPosition(null);
            setCustomMessagePosition(null);
          }}
          onAdd={(message, variables, position) => {
            handleAddCustomMessage(message, variables, position);
          }}
          availableVariables={availableVariables}
          position={customMessagePosition !== null ? customMessagePosition : getSafeDefaultPosition()}
          positionSuggestions={getPositionSuggestions()}
          onPositionChange={(newPosition) => {
            setCustomMessagePosition(newPosition);
          }}
          title="✏️ Add Custom Message"
          placeholder="Type your custom message... (use @ for variables)"
          buttonText="Add Custom Message"
          hasForbiddenOptions={getForbiddenEndforIndices().size > 0}
        />
      )}

      {/* Button Modal */}
      {showButtonModal && (
        <CustomMessageModal
          onClose={() => {
            setShowButtonModal(false);
            setSelectedPosition(null);
            setButtonPosition(null);
          }}
          onAdd={(message, variables, position) => {
            handleAddButton(message, variables, position);
          }}
          availableVariables={availableVariables}
          position={buttonPosition !== null ? buttonPosition : getSafeDefaultPosition()}
          positionSuggestions={getPositionSuggestions()}
          onPositionChange={(newPosition) => {
            setButtonPosition(newPosition);
          }}
          title="🔘 Add Button"
          placeholder="Enter button text... (use @ for variables)"
          buttonText="Add Button"
          isButton={true}
          hasForbiddenOptions={getForbiddenEndforIndices().size > 0}
        />
      )}

      {/* Button Group Modal */}
      {showButtonGroupModal && (
        <ButtonGroupModal
          onClose={() => {
            setShowButtonGroupModal(false);
            setSelectedPosition(null);
            setButtonGroupPosition(null);
          }}
          onAdd={(groupName, buttons, position) => {
            handleAddButtonGroup(groupName, buttons, position);
          }}
          position={buttonGroupPosition !== null ? buttonGroupPosition : getSafeDefaultPosition()}
          positionSuggestions={getPositionSuggestions()}
          onPositionChange={(newPosition) => {
            setButtonGroupPosition(newPosition);
          }}
          hasForbiddenOptions={getForbiddenEndforIndices().size > 0}
        />
      )}
    </div>
  );
};

export default TemplateEditor;